import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#4338ca';
  const lastUpdated = 'July 30, 2026';

  return (
    <LegalLayout app="AfterDose" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
      <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlabs ("we," "our," or "us") operates the AfterDose mobile application (the "App"). AfterDose is a small, private journal for the window after a medication adjustment: record the change once, check in daily for a minute, and bring a clear before-and-after summary to your next visit.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use the App. We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                AfterDose is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local-First Storage:</strong> Your adjustments, check-ins, and notes are stored locally on your device in a single private file.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party Tracking:</strong> We do not integrate any third-party analytics, tracking, or advertising SDKs.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Zero Network Requests:</strong> The App has no networking code, no backend, and no third-party SDKs. Nothing ever leaves your device unless you share a PDF yourself.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Medication Database:</strong> AfterDose is free-text only. It has no medication list and never suggests names or doses.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>
              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Adjustments &amp; Check-Ins
              </h3>
              <p className="mb-4 leading-relaxed">
                AfterDose stores the journal you keep, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Adjustment anchors: the medication name and doses in your own words, plus the date it changed</li>
                <li>Daily check-ins: six 0–4 ratings (focus, mood, sleep quality, appetite, anxiety, side-effect burden)</li>
                <li>Optional side-effect tags and one-line notes</li>
                <li>Visit questions and observation selections</li>
              </ul>
              <p className="leading-relaxed">
                Important: Entries are health-adjacent by nature — you may type medication names. They are stored locally only, never transmitted, and are not readable by other apps thanks to the iOS sandbox. The App requests no permissions at all — no HealthKit, photos, camera, microphone, or location access.
              </p>
              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Visit Summary PDF
              </h3>
              <p className="leading-relaxed">
                The prescriber Visit Summary is generated on your device and handed to the system share sheet only when you tap Export. The App keeps no copy of exported PDFs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage &amp; Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored as a single JSON file in the App's private Application Support container, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Remote Servers:</strong> Your check-ins and adjustment records are never uploaded to servers owned or operated by us.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Sandbox:</strong> The App operates within iOS's security sandbox, preventing other apps from accessing your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                AfterDose does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Health or medication data on any server — there is no server</li>
                <li>Apple Health / HealthKit data — the App neither reads from nor writes to it</li>
                <li>Your device location</li>
                <li>Contact information</li>
                <li>Photos or media files</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Network Connections
              </h2>
              <p className="leading-relaxed">
                AfterDose makes no network requests. The App has no backend, calls no external APIs, and works entirely offline. Nothing you enter ever leaves your device unless you explicitly export or share it yourself.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                AfterDose uses Apple's native frameworks only: SwiftUI, Foundation, and system PDF rendering. We do not integrate any third-party analytics tools, crash reporters, or advertising networks. AfterDose contains no in-app purchases and no ads.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights &amp; Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View:</strong> Browse the full timeline of check-ins, with day numbers counted from the adjustment.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Skip Freely:</strong> Skip any rating or any day — partial entries are fine.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Share On Your Terms:</strong> The Visit Summary leaves your device only when you export it.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                AfterDose is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/afterdose/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    AfterDose Support Page
                  </Link>
                </p>
              </div>
            </section>
      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
